import { FieldPath, FieldValues } from 'react-hook-form';
import { ControlledTokenInput, TokenOption } from './controlled-token-input';

type LanguageOption = TokenOption<string> & { flag: string };

// Values are BCP-47 tags, matching what the backend stores in `businessProfile.languages`
// (it normalizes names like "English" to tags on save). English carries its region because it
// drives the spelling of every generated page variant (en-US → "optimize", en-GB → "optimise").
const LANGUAGE_OPTIONS: LanguageOption[] = [
  { value: 'en-US', label: 'English (US)', flag: '🇺🇸' },
  { value: 'en-GB', label: 'English (UK)', flag: '🇬🇧' },
  { value: 'fr', label: 'French', flag: '🇫🇷' },
  { value: 'es', label: 'Spanish', flag: '🇪🇸' },
  { value: 'de', label: 'German', flag: '🇩🇪' },
  { value: 'it', label: 'Italian', flag: '🇮🇹' },
  { value: 'pt', label: 'Portuguese', flag: '🇵🇹' },
  { value: 'nl', label: 'Dutch', flag: '🇳🇱' },
  { value: 'pl', label: 'Polish', flag: '🇵🇱' },
  { value: 'ru', label: 'Russian', flag: '🇷🇺' },
  { value: 'ja', label: 'Japanese', flag: '🇯🇵' },
  { value: 'zh', label: 'Chinese', flag: '🇨🇳' },
  { value: 'ko', label: 'Korean', flag: '🇰🇷' },
  { value: 'ar', label: 'Arabic', flag: '🇸🇦' },
  { value: 'hi', label: 'Hindi', flag: '🇮🇳' },
  { value: 'tr', label: 'Turkish', flag: '🇹🇷' },
  { value: 'sv', label: 'Swedish', flag: '🇸🇪' },
  { value: 'da', label: 'Danish', flag: '🇩🇰' },
  { value: 'no', label: 'Norwegian', flag: '🇳🇴' },
  { value: 'fi', label: 'Finnish', flag: '🇫🇮' },
  { value: 'cs', label: 'Czech', flag: '🇨🇿' },
  { value: 'el', label: 'Greek', flag: '🇬🇷' },
  { value: 'he', label: 'Hebrew', flag: '🇮🇱' },
  { value: 'th', label: 'Thai', flag: '🇹🇭' },
  { value: 'vi', label: 'Vietnamese', flag: '🇻🇳' },
  { value: 'id', label: 'Indonesian', flag: '🇮🇩' },
  { value: 'ms', label: 'Malay', flag: '🇲🇾' },
  { value: 'uk', label: 'Ukrainian', flag: '🇺🇦' },
  { value: 'ro', label: 'Romanian', flag: '🇷🇴' },
  { value: 'hu', label: 'Hungarian', flag: '🇭🇺' },
  { value: 'bg', label: 'Bulgarian', flag: '🇧🇬' },
];

// Legacy values still stored on older brands, rendered but not offered as new choices: a bare
// `en` (extracted before the region was requested) and language names the backend migration
// didn't know about.
const LEGACY_LANGUAGE_DISPLAY: Record<string, string> = {
  en: '🌐 English',
};

const getLanguageDisplay = (tag: string): string => {
  const exact = LANGUAGE_OPTIONS.find((l) => l.value === tag);
  if (exact) return `${exact.flag} ${exact.label}`;
  const legacy = LEGACY_LANGUAGE_DISPLAY[tag];
  if (legacy) return legacy;
  // Unknown regional tag (e.g. `fr-CA`, `pt-BR`): reuse the base language's flag and label and keep
  // the region visible, rather than showing a raw code.
  const base = LANGUAGE_OPTIONS.find((l) => l.value === tag.split('-')[0]);
  if (base && tag.includes('-')) return `${base.flag} ${base.label} (${tag.split('-')[1]})`;
  return tag;
};

type ControlledLanguageTokenInputProps<TForm extends FieldValues> = {
  name: FieldPath<TForm>;
  label?: string;
  placeholder?: string;
  direction?: 'horizontal' | 'vertical';
  className?: string;
};

export function ControlledLanguageTokenInput<TForm extends FieldValues>(
  props: ControlledLanguageTokenInputProps<TForm>,
) {
  const { name, label, placeholder = 'Add language...', direction, className } = props;

  return (
    <ControlledTokenInput<TForm, string>
      name={name}
      label={label}
      placeholder={placeholder}
      direction={direction}
      className={className}
      forceSelection
      source={{ type: 'static', options: LANGUAGE_OPTIONS }}
      renderToken={(iso) => <span>{getLanguageDisplay(iso)}</span>}
    />
  );
}

export { LANGUAGE_OPTIONS, getLanguageDisplay };
